import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import api from '../services/api';
import '../styles/VendorDashboard.css';

interface DashboardStats {
  activeJobs: number;
  inProgressJobs: number;
  completedJobs: number;
  pendingJobs: number;
  totalDrivers: number;
  onlineDrivers: number;
}

const VendorDashboard: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    setIsLoading(true);
    try {
      const data = await api.getVendorDashboardStats();
      setStats(data);
      setError('');
    } catch (err: any) {
      setError('Failed to load dashboard stats');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="vendor-dashboard">
        <div className="loading">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="vendor-dashboard">
      <div className="dashboard-header">
        <div>
          <h1>Welcome, {user?.firstName}!</h1>
          <p className="business-name">{user?.businessName} - {user?.city}</p>
        </div>
        <button className="refresh-button" onClick={fetchDashboardStats}>
          🔄 Refresh
        </button>
      </div>

      {error && <div className="error-box">{error}</div>}

      {/* Stats Cards */}
      <div className="stats-grid">
        <Link to="/vendor-fleet" className="stat-card fleet-card">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <h3>Fleet Drivers</h3>
            <div className="stat-value">{stats?.totalDrivers || 0}</div>
            <div className="stat-subtitle">{stats?.onlineDrivers || 0} online</div>
          </div>
        </Link>

        <Link to="/vendor-jobs?status=pending" className="stat-card pending-card">
          <div className="stat-icon">📋</div>
          <div className="stat-content">
            <h3>Pending Jobs</h3>
            <div className="stat-value">{stats?.pendingJobs || 0}</div>
            <div className="stat-subtitle">Awaiting assignment</div>
          </div>
        </Link>

        <Link to="/vendor-jobs?status=active" className="stat-card active-card">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <h3>Active Jobs</h3>
            <div className="stat-value">{stats?.activeJobs || 0}</div>
            <div className="stat-subtitle">Accepted by drivers</div>
          </div>
        </Link>

        <Link to="/vendor-jobs?status=in-progress" className="stat-card progress-card">
          <div className="stat-icon">🚗</div>
          <div className="stat-content">
            <h3>In Progress</h3>
            <div className="stat-value">{stats?.inProgressJobs || 0}</div>
            <div className="stat-subtitle">Currently ongoing</div>
          </div>
        </Link>

        <Link to="/vendor-jobs?status=completed" className="stat-card completed-card">
          <div className="stat-icon">✔️</div>
          <div className="stat-content">
            <h3>Completed</h3>
            <div className="stat-value">{stats?.completedJobs || 0}</div>
            <div className="stat-subtitle">Successfully finished</div>
          </div>
        </Link>

        <Link to="/vendor-earnings" className="stat-card earnings-card">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <h3>Earnings</h3>
            <div className="stat-value">View</div>
            <div className="stat-subtitle">Transaction history</div>
          </div>
        </Link>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h2>Quick Actions</h2>
        <div className="action-buttons">
          <Link to="/vendor-fleet" className="action-btn">
            👥 Manage Fleet
          </Link>
          <Link to="/vendor-jobs" className="action-btn">
            📋 View All Jobs
          </Link>
          <Link to="/vendor-earnings" className="action-btn">
            💰 View Earnings
          </Link>
        </div>
      </div>

      {/* Info Section */}
      <div className="info-section">
        <h2>How It Works</h2>
        <div className="info-grid">
          <div className="info-card">
            <div className="info-number">1</div>
            <h3>Manage Your Fleet</h3>
            <p>Add and manage drivers in your fleet. Drivers can enroll using your phone number and OTP.</p>
          </div>
          <div className="info-card">
            <div className="info-number">2</div>
            <h3>Receive Job Offers</h3>
            <p>When customers request services, jobs are offered to your available drivers automatically.</p>
          </div>
          <div className="info-card">
            <div className="info-number">3</div>
            <h3>Assign Jobs</h3>
            <p>Manually assign pending jobs to specific drivers in your fleet for better control.</p>
          </div>
          <div className="info-card">
            <div className="info-number">4</div>
            <h3>Track Earnings</h3>
            <p>All earnings from jobs completed by your drivers are tracked in your account.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorDashboard;
